import React, { useState } from 'react';
import {
  Typography,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import SpillsCharts from '../charts/SpillsCharts';
import FullScreenTable from '../tables/FullScreenTable';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import logger from '../../utils/logger';

const SpillsTab = ({
  searchData,
  getCurrentTableCounts,
  getCurrentTableData,
  getCurrentTableType,
  spillsTableSort,
  handleSpillsTableSort,
  resetSpillsTableSort,
  hiddenSpillLines,
  setHiddenSpillLines,
  resetHiddenSpillLines,
  hiddenSpillVolumeLines,
  setHiddenSpillVolumeLines,
  resetHiddenSpillVolumeLines,
  tierInfo,
}) => {
  // Local state for operator names popup
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Local state for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  // Don't render if not spills tab or no data
  if (getCurrentTableType() !== 'spills' || !getCurrentTableData() || getCurrentTableData().length === 0) {
    return null;
  }

  const spillsData = getCurrentTableData();
  
  // Calculate unique operator names for this tab only
  const getTabOperatorNames = () => {
    if (!spillsData || spillsData.length === 0) return [];
    
    const operatorNames = new Set();
    spillsData.forEach(record => {
      const operator = record.operator_name || record['Operator Name'] || record.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  };
  
  // Calculate total spills
  const totalSpills = spillsData.length;
  
  // Calculate date range
  const dateColumn = spillsData[0]['Date of Loss'] !== undefined ? 'Date of Loss' :
                   spillsData[0].date_of_loss !== undefined ? 'date_of_loss' : null;
  
  let dateRangeText = '';
  if (dateColumn) {
    const dates = spillsData
      .map(record => record[dateColumn])
      .filter(date => date)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));
    
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };
      
      // Check if dates are in the same month/year
      if (minDate.getFullYear() === maxDate.getFullYear() && minDate.getMonth() === maxDate.getMonth()) {
        dateRangeText = ` (${formatDate(minDate)})`;
      } else {
        dateRangeText = `, with occurances from ${formatDate(minDate)} to ${formatDate(maxDate)}`;
      }
    }
  }
  
  // Calculate total spill volume
  const volumeColumn = spillsData[0]['Gross Loss'] !== undefined ? 'Gross Loss' :
                     spillsData[0].gross_loss !== undefined ? 'gross_loss' : null;
  
  let totalVolume = 0;
  if (volumeColumn) {
    totalVolume = spillsData.reduce((sum, record) => {
      const volume = parseFloat(record[volumeColumn]) || 0;
      return sum + volume;
    }, 0);
  }
  
  // Calculate average spill volume
  const averageVolume = totalSpills > 0 ? totalVolume / totalSpills : 0;
  
  // Calculate recovered volume
  const recoveredColumn = spillsData[0]['Recovered'] !== undefined ? 'Recovered' :
                        spillsData[0].recovered !== undefined ? 'recovered' : null;
  
  let recoveredVolume = 0;
  if (recoveredColumn) {
    recoveredVolume = spillsData.reduce((sum, record) => {
      const recovered = parseFloat(record[recoveredColumn]) || 0;
      return sum + recovered;
    }, 0);
  }
  
  // Calculate net loss
  const netLossColumn = spillsData[0]['Net Loss'] !== undefined ? 'Net Loss' :
                      spillsData[0].net_loss !== undefined ? 'net_loss' : null;
  
  let netLossVolume = 0;
  if (netLossColumn) {
    netLossVolume = spillsData.reduce((sum, record) => {
      const netLoss = parseFloat(record[netLossColumn]) || 0;
      return sum + netLoss;
    }, 0);
  }
  
  // Calculate recovery percentage
  const recoveryPercentage = totalVolume > 0 ? (recoveredVolume / totalVolume) * 100 : 0;
  const lostPercentage = totalVolume > 0 ? (netLossVolume / totalVolume) * 100 : 0;
  
  // Calculate water impact spills
  const waterImpactColumn = spillsData[0]['Water Impact'] !== undefined ? 'Water Impact' :
                          spillsData[0].water_impact !== undefined ? 'water_impact' : null;
  
  let waterImpactSpills = 0;
  let waterImpactVolume = 0;
  if (waterImpactColumn) {
    const waterImpactRecords = spillsData.filter(record => {
      const impact = record[waterImpactColumn];
      return impact && impact.toString().toUpperCase() === 'YES';
    });
    
    waterImpactSpills = waterImpactRecords.length;
    
    if (volumeColumn) {
      waterImpactVolume = waterImpactRecords.reduce((sum, record) => {
        const volume = parseFloat(record[volumeColumn]) || 0;
        return sum + volume;
      }, 0);
    }
  }
  
  // Calculate top cause with percentage
  const causeColumn = spillsData[0]['Cause of Loss'] !== undefined ? 'Cause of Loss' :
                    spillsData[0].cause_of_loss !== undefined ? 'cause_of_loss' : null;
  
  let topCause = 'Unknown';
  let topCausePercentage = 0;
  if (causeColumn) {
    const causeCounts = {};
    spillsData.forEach(record => {
      const cause = record[causeColumn];
      if (cause && cause.toString().trim() !== '') {
        const causeStr = cause.toString().trim();
        causeCounts[causeStr] = (causeCounts[causeStr] || 0) + 1;
      }
    });
    
    const sortedCauses = Object.entries(causeCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    
    if (sortedCauses.length > 0) {
      topCause = sortedCauses[0][0];
      topCausePercentage = ((sortedCauses[0][1] / totalSpills) * 100).toFixed(1);
    }
  }
  
  // Calculate top facility with percentage
  const facilityColumn = spillsData[0]['Facility Type'] !== undefined ? 'Facility Type' :
                       spillsData[0].facility_type !== undefined ? 'facility_type' : null;
  
  let topFacility = 'Unknown';
  let topFacilityPercentage = 0;
  if (facilityColumn) {
    const facilityCounts = {};
    spillsData.forEach(record => {
      const facility = record[facilityColumn];
      if (facility && facility.toString().trim() !== '') {
        const facilityStr = facility.toString().trim();
        facilityCounts[facilityStr] = (facilityCounts[facilityStr] || 0) + 1;
      }
    });
    
    const sortedFacilities = Object.entries(facilityCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    
    if (sortedFacilities.length > 0) {
      topFacility = sortedFacilities[0][0];
      topFacilityPercentage = ((sortedFacilities[0][1] / totalSpills) * 100).toFixed(1);
    }
  }
  
  // Calculate top liquid type with percentage
  const liquidTypeColumn = spillsData[0]['Liquid Type'] !== undefined ? 'Liquid Type' :
                         spillsData[0].liquid_type !== undefined ? 'liquid_type' : null;
  
  let topLiquidType = 'Unknown';
  let topLiquidTypePercentage = 0;
  if (liquidTypeColumn) {
    const liquidTypeCounts = {};
    spillsData.forEach(record => {
      const liquidType = record[liquidTypeColumn];
      if (liquidType && liquidType.toString().trim() !== '') {
        const liquidTypeStr = liquidType.toString().trim();
        liquidTypeCounts[liquidTypeStr] = (liquidTypeCounts[liquidTypeStr] || 0) + 1;
      }
    });
    
    const sortedLiquidTypes = Object.entries(liquidTypeCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    
    if (sortedLiquidTypes.length > 0) {
      topLiquidType = sortedLiquidTypes[0][0];
      topLiquidTypePercentage = ((sortedLiquidTypes[0][1] / totalSpills) * 100).toFixed(1);
    }
  }

  return (
    <>
      {/* Spills Findings Card */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Spills
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="About Spills" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setAboutDialogOpen(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Raw Data" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setFullScreenTableOpen(true)}
                  disabled={tierInfo && !tierInfo.can_view_raw_data}
                  sx={{
                    opacity: (tierInfo && !tierInfo.can_view_raw_data) ? 0.5 : 1,
                    cursor: (tierInfo && !tierInfo.can_view_raw_data) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Content */}
          <Box>
            {/* Unique Operator Names */}
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>
                  Unique Operator Names — {getTabOperatorNames().length.toLocaleString()}
                </Typography>
                <Tooltip title="View all unique operator names" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOperatorNamesDialogOpen(true)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Divider */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
            
            {/* Findings Content */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The spills data review process identified{' '}
                <strong>{totalSpills.toLocaleString()}</strong> reported spill{totalSpills !== 1 ? 's' : ''} associated with the subject assets{dateRangeText}, and an average spill volume of{' '}
                <strong>{averageVolume.toFixed(0)}</strong> barrels. Of the total reported spill volume of{' '}
                <strong>{totalVolume.toLocaleString()}</strong> barrel{totalVolume !== 1 ? 's' : ''},{' '}
                <strong>{recoveryPercentage.toFixed(1)}%</strong> was recovered, while{' '}
                <strong>{lostPercentage.toFixed(1)}%</strong> was lost.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The review further identified{' '}
                <strong>{waterImpactSpills.toLocaleString()}</strong> spill{waterImpactSpills !== 1 ? 's' : ''} which impacted water sources{waterImpactSpills > 0 ? `, with an associated gross volume of ${waterImpactVolume.toLocaleString()} spilled barrels` : ''}.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                <strong>{topCause} ({topCausePercentage}%)</strong> was identified as the leading cause of spill occurance, while the <strong>{topFacility} ({topFacilityPercentage}%)</strong> was the top spill occurance facility and <strong>{topLiquidType} ({topLiquidTypePercentage}%)</strong> the highest occurance liquid type spilled.
              </Typography>

              {/* Analytics Scope Notice */}
              {getCurrentTableData().length < getCurrentTableCounts() && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Analytics are based on {getCurrentTableData().length.toLocaleString()} of {getCurrentTableCounts().toLocaleString()} identified records 
                    (limited to first {getCurrentTableData().length.toLocaleString()} rows).
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
          
          {tierInfo && !tierInfo.can_view_raw_data && (
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <button 
                onClick={() => setUpgradeDialogOpen(true)}
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to view raw data tables
              </button>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Spills Charts */}
      <SpillsCharts
        spillsData={getCurrentTableData()}
        onSortSpillDetails={handleSpillsTableSort}
        spillDetailsSortField={spillsTableSort.field}
        spillDetailsSortDirection={spillsTableSort.direction}
        onResetSpillDetails={resetSpillsTableSort}
        hiddenSpillLines={hiddenSpillLines}
        setHiddenSpillLines={setHiddenSpillLines}
        resetHiddenSpillLines={resetHiddenSpillLines}
        hiddenSpillVolumeLines={hiddenSpillVolumeLines}
        setHiddenSpillVolumeLines={setHiddenSpillVolumeLines}
        resetHiddenSpillVolumeLines={resetHiddenSpillVolumeLines}
      />

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          About Spills
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              For all producers and transporters of crude oil and casinghead gas, state regulations require immediate notification to be given of a fire, leak, spill, or break in facilities causing a loss of more than five (5) barrels of crude oil, gas well liquids, or associated products. 
              Notice shall be followed by a letter giving details of the loss after appropriate measures have been taken.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Names Dialog */}
      <Dialog
        open={operatorNamesDialogOpen}
        onClose={() => setOperatorNamesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Unique Operator Names
            </Typography>
            <IconButton
              onClick={() => setOperatorNamesDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing unique operator names from current search results ({getCurrentTableData().length.toLocaleString()} records)
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getTabOperatorNames().length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total: {getTabOperatorNames().length.toLocaleString()} unique operators</strong>
                </Typography>
                <List dense>
                  {getTabOperatorNames().map((operator, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={operator} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography>No operator data available</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Full Screen Table Dialog */}
      <FullScreenTable
        open={fullScreenTableOpen}
        onClose={() => setFullScreenTableOpen(false)}
        tableType="spills"
        data={spillsData}
        allColumns={spillsData.length > 0 ? Object.keys(spillsData[0]) : []}
        defaultColumns={['operator_name', 'lease_name', 'date_of_loss', 'gross_loss', 'net_loss']}
        totalCount={getCurrentTableCounts()}
        rowLimit={tierInfo?.table_limits?.spills || 0}
        onReset={() => {
          // Reset any spills-specific state if needed
          logger.debug('Spills table reset');
        }}
      />
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default SpillsTab;